import React, { memo, useMemo, useEffect } from 'react';
import { View } from 'react-native';
import Amount from 'design-system/Amount';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Status from 'design-system/Status';
import useColors from 'hooks/useColors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getIncomes } from 'actions';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { Button } from 'design-system';
import { RollingBudgetsTooltip } from './RollingBudgetsTooltip';
import { isWeb } from '../../../constants';
import { RollingBudgetStatusTooltip } from './RollingBudgetStatusTooltip';
import { isRolloverActive, selectBudgets } from '../selectors';
import { useSaveBudget } from '../hooks/useSaveBudget';
import { getBudgets } from '../actions';
import { useBudgetRollover } from '../hooks/useBudgetRollover';
export const BudgetRollingAmountLabel = memo(({ rollingAccumulatedLimit, currency, defaultBudget, isLatest, showFullData, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const isRollover = useAppSelector(isRolloverActive);
    const isCurrentPeriod = isLatest;
    const isRolledAmount = !!rollingAccumulatedLimit;
    const { setRollover, setSaveHandler } = useBudgetRollover();
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const isUnlocked = isUnlockedForSpace || showFullData;
    const [saving, save] = useSaveBudget();
    const dispatch = useAppDispatch();
    const budgets = useAppSelector(selectBudgets);
    useEffect(() => {
        const saveBudgets = async () => {
            await save(undefined, true, budgets);
            dispatch(getBudgets());
            dispatch(getIncomes());
        };
        setSaveHandler(() => saveBudgets());
    }, [budgets, dispatch, save, setSaveHandler]);
    const textDescription = isUnlocked
        ? 'Want to continue rolling?'
        : 'Want to see rolled amount?';
    const rollingButton = useMemo(() => {
        const buttonTitle = isUnlocked ? 'Turn on' : 'Upgrade';
        return (<View style={styles.buttonContainer}>
          <Button title={buttonTitle} brandReversed square isFetching={saving} cards hug onPress={isUnlocked ? () => setRollover(true) : () => onPressUpgrade()}/>
        </View>);
    }, [
        isUnlocked,
        onPressUpgrade,
        saving,
        setRollover,
        styles.buttonContainer,
    ]);
    const rolledAmountItem = useMemo(() => {
        if (!isRolledAmount) {
            return null;
        }
        return (<View style={styles.amount}>
          <Text TextThin-14 Secondary>
            Rolled{' '}
            <Amount NumbersThin-14 showSign Secondary value={{
                value: rollingAccumulatedLimit,
                currency,
            }}/>
          </Text>
        </View>);
    }, [currency, isRolledAmount, rollingAccumulatedLimit, styles.amount]);
    const rollingStatus = useMemo(() => {
        if (isRollover && isCurrentPeriod) {
            const tooltip = isRolledAmount ? (<RollingBudgetStatusTooltip rolledAmount={rollingAccumulatedLimit} defaultAmount={defaultBudget || 0} currency={currency} wasRolling={false}/>) : (<RollingBudgetsTooltip offset={0}/>);
            if (!isRolledAmount) {
                return (<View style={styles.statusAndTooltip}>
              <View>
                <View style={styles.statusText}>
                  <Status activeText="Rolling active" active={isRollover} inactiveText={'Rolling inactive'}/>
                </View>
              </View>
              <Spacer w={8}/>
              {tooltip}
            </View>);
            }
            return (<View style={styles.statusAndTooltip}>
            <View>
              <View style={styles.statusText}>
                <Status activeText="Rolling active" active={isRollover} inactiveText={'Rolling inactive'}/>
              </View>
              {rolledAmountItem}
            </View>
            <Spacer w={8}/>
            {tooltip}
          </View>);
        }
        if (isRolledAmount) {
            if (!isRollover && !showFullData) {
                return (<View style={styles.statusAndTooltip}>
              <View>
                <View style={styles.statusText}>
                  <Text Text-14 Gray>
                    Rolling was on
                  </Text>
                  <Spacer w={8}/>
                  <RollingBudgetsTooltip offset={0}/>
                </View>
                <Text TextThin-14 Secondary>
                  {textDescription}
                </Text>
                {rollingButton}
              </View>
            </View>);
            }
            return (<View style={styles.statusAndTooltip}>
            <View>
              <View style={styles.statusText}>
                <Text Text-14 color={colors.status.positive}>
                  Rolling was on
                </Text>
              </View>
              {rolledAmountItem}
            </View>
            <Spacer w={8}/>
            <RollingBudgetStatusTooltip rolledAmount={rollingAccumulatedLimit} defaultAmount={defaultBudget || 0} currency={currency} wasRolling/>
          </View>);
        }
        return null;
    }, [
        colors.status.positive,
        currency,
        defaultBudget,
        isCurrentPeriod,
        isRolledAmount,
        isRollover,
        rolledAmountItem,
        rollingAccumulatedLimit,
        rollingButton,
        showFullData,
        styles.statusAndTooltip,
        styles.statusText,
        textDescription,
    ]);
    if (!isRolledAmount && !isCurrentPeriod) {
        return null;
    }
    return (<View style={styles.mainContainer}>
        <View style={styles.container}>{rollingStatus}</View>
      </View>);
});
const styleSet = createStyleSheets(() => ({
    container: {
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
        paddingBottom: rem(8),
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    statusText: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    amount: {
        alignItems: 'flex-end',
    },
    statusAndTooltip: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    mainContainer: {
        ...(isWeb && {
            paddingTop: rem(8),
        }),
    },
    buttonContainer: {
        marginTop: rem(4),
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
}));
